import { Button } from "@clockwise/design-system";
import { ResponseStatusEnum } from "@clockwise/schema";
import { CircularProgress } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import React from "react";

// Type guard to ensure that the responseStatus is a valid option
export const isRespondable = (
  responseStatus?: ResponseStatusEnum,
  isCancelled?: boolean,
): responseStatus is keyof typeof responseOptions => {
  if (isCancelled) {
    return false;
  }

  if (!responseStatus) {
    return false;
  }

  if (responseStatus === ResponseStatusEnum.NeedsAction) {
    return true;
  }

  if (responseStatus in responseOptions) {
    return (responseStatus as keyof typeof responseOptions) !== undefined;
  }

  return false;
};

const responseOptions = {
  [ResponseStatusEnum.Accepted]: { label: "Yes", value: ResponseStatusEnum.Accepted },
  [ResponseStatusEnum.Declined]: { label: "No", value: ResponseStatusEnum.Declined },
  [ResponseStatusEnum.Tentative]: { label: "Maybe", value: ResponseStatusEnum.Tentative },
};

export type Responses = keyof typeof responseOptions | ResponseStatusEnum.NeedsAction;

type Props = {
  onSelect: (responseStatus: ResponseStatusEnum) => void;
  selected?: Responses | null;
  selecting?: Responses | null;
};

export const EventResponseControl = ({ onSelect, selected = null, selecting = null }: Props) => {
  const loading = !selected;

  return (
    <div className="cw-p-[15px] cw-flex cw-gap-3 cw-items-center cw-align-middle cw-mb-2">
      <div className="cw-heading-base">Going?</div>
      {loading ? (
        <div className="cw-ml-2 cw-w-full">
          <Skeleton variant="text" width="100%" height="2em" />
        </div>
      ) : (
        Object.values(responseOptions).map(({ label, value }) => (
          <EventUpdateResponseButton
            disabled={!!selecting || selected === value}
            isSaving={selecting === value}
            key={value}
            label={label}
            onClick={() => onSelect(value)}
          />
        ))
      )}
    </div>
  );
};

const EventUpdateResponseButton = ({
  isSaving = false,
  label,
  disabled = false,
  onClick,
}: {
  isSaving?: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
}) => {
  return (
    <Button onClick={onClick} disabled={disabled} variant="outlined">
      {isSaving ? <CircularProgress className="cw-w-4 cw-h-4" /> : label}
    </Button>
  );
};

export default EventResponseControl;
